<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('adhesion.createSubtitle') }} {{ $t('adhesion.requiredFieldsNote') }}</span>
      </div>
    </div>

    <div class="ov-grid">
      <!-- ===== FORMULAIRE ===== -->
      <div class="ov-card">
        <div class="ov-card__head">
          <div class="ov-card__title">{{ $t('adhesion.companyInfo') }}</div>
        </div>

        <div class="ov-card__body">
          <!-- 1. Identité -->
          <div class="ca-section">
            <span class="ca-section__num">1</span>
            {{ $t('adhesion.companyIdentity') }}
          </div>
          <div class="ov-row">
            <div class="ov-field">
              <label class="ov-label" for="ca-raison">{{ $t('adhesion.businessName') }}<span class="ov-required">*</span></label>
              <input
                id="ca-raison"
                v-model.trim="$v.compagnieTransport.raisonSociale.$model"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': raisonSocialeCompagnieErrors.length }"
                :placeholder="$t('adhesion.form.businessNamePlaceholder')"
              />
              <div v-if="raisonSocialeCompagnieErrors.length" class="ov-error">{{ raisonSocialeCompagnieErrors[0] }}</div>
            </div>

            <div class="ov-field">
              <label class="ov-label" for="ca-sigle">{{ $t('adhesion.acronym') }}<span class="ov-required">*</span></label>
              <input
                id="ca-sigle"
                v-model.trim="$v.compagnieTransport.sigle.$model"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': sigleCompagnieErrors.length }"
                :placeholder="$t('adhesion.acronymPlaceholder')"
              />
              <div v-if="sigleCompagnieErrors.length" class="ov-error">{{ sigleCompagnieErrors[0] }}</div>
            </div>

            <div class="ov-field ov-col-full">
              <label class="ov-label" for="ca-description">{{ $t('adhesion.description') }}</label>
              <textarea
                id="ca-description"
                v-model.trim="compagnieTransport.description"
                class="ov-textarea"
                rows="3"
                :placeholder="$t('adhesion.form.descriptionPlaceholder')"
              ></textarea>
            </div>
          </div>

          <!-- 2. Contact -->
          <div class="ca-section ca-section--spaced">
            <span class="ca-section__num">2</span>
            {{ $t('adhesion.contactLocation') }}
          </div>
          <div class="ov-row">
            <div class="ov-field">
              <label class="ov-label" for="ca-email">{{ $t('adhesion.yourEmail') }}<span class="ov-required">*</span></label>
              <input
                id="ca-email"
                v-model.trim="$v.compagnieTransport.email.$model"
                type="email"
                class="ov-input"
                :class="{ 'is-invalid': emailCompagnieErrors.length }"
                placeholder="contact@compagnie.com"
                autocomplete="off"
              />
              <div v-if="emailCompagnieErrors.length" class="ov-error">{{ emailCompagnieErrors[0] }}</div>
            </div>

            <div class="ov-field">
              <label class="ov-label" for="ca-phone">{{ $t('adhesion.phoneNumber') }}<span class="ov-required">*</span></label>
              <div class="ov-input-group">
                <span class="ov-input-prefix">+225</span>
                <input
                  id="ca-phone"
                  v-model.trim="$v.compagnieTransport.telephone.$model"
                  type="tel"
                  inputmode="tel"
                  class="ov-input"
                  :class="{ 'is-invalid': telephoneCompagnieErrors.length }"
                  :placeholder="$t('adhesion.phonePlaceholder')"
                />
              </div>
              <div v-if="telephoneCompagnieErrors.length" class="ov-error">{{ telephoneCompagnieErrors[0] }}</div>
            </div>

            <div class="ov-field ov-col-full">
              <label class="ov-label" for="ca-ville">{{ $t('adhesion.locality') }}<span class="ov-required">*</span></label>
              <select
                id="ca-ville"
                v-model="$v.compagnieTransport.villeDesignation.$model"
                class="ov-select"
                :class="{ 'is-invalid': villeCompagnieErrors.length }"
              >
                <option :value="null" disabled>{{ $t('adhesion.selectLocality') }}</option>
                <option v-for="(ville, i) in villesList" :key="i" :value="ville.designation">
                  {{ ville.designation }}
                </option>
              </select>
              <div v-if="villeCompagnieErrors.length" class="ov-error">{{ villeCompagnieErrors[0] }}</div>
            </div>
          </div>

          <!-- 3. Documents -->
          <div class="ca-section ca-section--spaced">
            <span class="ca-section__num">3</span>
            {{ $t('adhesion.documentsToProvide') }}
            <span class="ca-section__optional">{{ $t('adhesion.form.optional') }}</span>
          </div>
          <div class="ov-row">
            <!-- Logo -->
            <div class="ov-field">
              <div class="ov-label">{{ $t('adhesion.logo') }}</div>
              <div
                class="ca-drop"
                :class="{ 'ca-drop--filled': logoSelected, 'ca-drop--over': dragOver === 'logo' }"
                role="button"
                tabindex="0"
                @click="!logoSelected && $refs.logoInput.click()"
                @keyup.enter="!logoSelected && $refs.logoInput.click()"
                @dragover.prevent="dragOver = 'logo'"
                @dragleave.prevent="dragOver = null"
                @drop.prevent="onDrop($event, 'logo')"
              >
                <img v-if="logoPreviewUrl" :src="logoPreviewUrl" class="ca-drop__img" alt="Logo" />
                <div v-else class="ca-drop__placeholder">
                  <v-icon size="28">mdi-image-plus</v-icon>
                  <span>{{ $t('adhesion.clickToAddLogo') }}</span>
                  <small>{{ $t('adhesion.form.logoFormats') }}</small>
                </div>
              </div>
              <div v-if="logoSelected" class="ca-file">
                <span class="ca-file__name">{{ logoSelected.name }}</span>
                <span class="ca-file__size">{{ formatSize(logoSelected.size) }}</span>
                <button type="button" class="ca-file__action" @click="$refs.logoInput.click()">{{ $t('adhesion.form.replace') }}</button>
                <button type="button" class="ca-file__action ca-file__action--danger" @click="clearLogo">{{ $t('adhesion.form.remove') }}</button>
              </div>
              <input ref="logoInput" type="file" accept="image/png,image/jpeg" style="display: none" @change="onLogoChange" />
            </div>

            <!-- Attestation -->
            <div class="ov-field">
              <div class="ov-label">{{ $t('adhesion.certificate') }}</div>
              <div
                class="ca-drop"
                :class="{ 'ca-drop--filled': attestationSelected, 'ca-drop--over': dragOver === 'attestation' }"
                role="button"
                tabindex="0"
                @click="!attestationSelected && $refs.attestationInput.click()"
                @keyup.enter="!attestationSelected && $refs.attestationInput.click()"
                @dragover.prevent="dragOver = 'attestation'"
                @dragleave.prevent="dragOver = null"
                @drop.prevent="onDrop($event, 'attestation')"
              >
                <iframe
                  v-if="attestationPreviewUrl"
                  class="ca-drop__pdf"
                  :src="attestationPreviewUrl"
                  :title="$t('adhesion.certificatePreview')"
                />
                <div v-else class="ca-drop__placeholder">
                  <v-icon size="28">mdi-file-pdf-box</v-icon>
                  <span>{{ $t('adhesion.clickToAddCertificate') }}</span>
                  <small>{{ $t('adhesion.form.certificateFormats') }}</small>
                </div>
              </div>
              <div v-if="attestationSelected" class="ca-file">
                <span class="ca-file__name">{{ attestationSelected.name }}</span>
                <span class="ca-file__size">{{ formatSize(attestationSelected.size) }}</span>
                <button type="button" class="ca-file__action" @click="$refs.attestationInput.click()">{{ $t('adhesion.form.replace') }}</button>
                <button type="button" class="ca-file__action ca-file__action--danger" @click="clearAttestation">{{ $t('adhesion.form.remove') }}</button>
              </div>
              <input ref="attestationInput" type="file" accept="application/pdf" style="display: none" @change="onAttestationChange" />
            </div>
          </div>
          <div v-if="fileError" class="ov-error">{{ fileError }}</div>
        </div>

        <div class="ov-card__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="overlay" @click="reinitForm">
            {{ $t('adhesion.reset') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" :disabled="overlay" @click="submitForm">
            <v-icon small>mdi-send</v-icon>
            {{ $t('adhesion.submitRequest') }}
          </button>
        </div>
      </div>

      <!-- ===== RÉCAPITULATIF ===== -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__kicker">{{ $t('adhesion.form.summary') }}</div>
              <div class="ov-card__title">{{ compagnieTransport.raisonSociale || $t('adhesion.form.newCompany') }}</div>
            </div>
          </div>
          <div class="ov-card__body">
            <div class="ov-progress mb-4"><span :style="{ width: completion + '%' }"></span></div>
            <ul class="ca-checklist">
              <li
                v-for="item in checklist"
                :key="item.label"
                class="ca-checklist__item"
                :class="{ 'ca-checklist__item--done': item.done }"
              >
                <span class="ca-checklist__mark">
                  <v-icon v-if="item.done" x-small color="white">mdi-check</v-icon>
                </span>
                <span class="ca-checklist__label">{{ item.label }}</span>
                <span v-if="item.optional" class="ca-checklist__optional">{{ $t('adhesion.form.optional') }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-warning" type="warning" width="350px" dismissible>{{ warningMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_CREER_COMPAGNIE_TRANSPORT,
  API_OBTENIR_LISTE_DES_VILLES_DISPONIBLE,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import {
  required,
  email,
} from "vuelidate/lib/validators";
import {
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
} from "../messages/messageProcess";
export default {
  name: "CreerDemandeAdhesionInterne",
  data() {
    return {
      successMsg: null,
      errorMsg: null,
      warningMsg: null,
      overlay: false,

      logoSelected: null,
      attestationSelected: null,

      logoInBase64: null,
      attestionInBase64: null,

      logoPreviewUrl: null,
      attestationPreviewUrl: null,

      // Zone de dépôt survolée pendant un glisser-déposer ('logo' | 'attestation' | null)
      dragOver: null,
      fileError: null,

      defaultObject: {},
      villesList: [],

      objectContainList: {
        datas: [],
      },

      compagnieTransport: {
        description: null,
        raisonSociale: null,
        email: null,
        sigle: null,
        telephone: null,
        villeDesignation: null,
        documentDTOS: [],
      },

      attestationTransportDocumentModel: {
        compagnieTransportRaisonSociale: null,
        typeDocument: null,
        content: null,
        typeMime: null,
      },

      logoDocumentModel: {
        compagnieTransportRaisonSociale: null,
        typeDocument: null,
        content: null,
        typeMime: null,
      },
    };
  },

  validations: {
    compagnieTransport: {
      
      email: {
        required,
        email,
      },

      raisonSociale: {
        required,
      },

      sigle: {
        required,
      },

      villeDesignation: {
        required,
      },

      telephone: {
        required,
      },
    },
  },

  methods: {
    reinitForm() {
      this.compagnieTransport.description = null;
      this.compagnieTransport.email = null;
      this.compagnieTransport.raisonSociale = null;
      this.compagnieTransport.sigle = null;
      this.compagnieTransport.telephone = null;
      this.compagnieTransport.villeDesignation = null;

      this.logoSelected = null;
      this.attestationSelected = null;
      this.compagnieTransport.documentDTOS = [];
      this.logoDocumentModel.content = null;
      this.attestationTransportDocumentModel.content = null;
      this.$v.$reset();

      if (this.$refs.logoInput) this.$refs.logoInput.value = '';
      if (this.$refs.attestationInput) this.$refs.attestationInput.value = '';
      this.fileError = null;

      this.revokePreviewUrls();
    },

    onLogoChange(e) {
      const file = e.target.files && e.target.files[0];
      this.setFile(file, 'logo');
    },

    // CONTRÔLE DU FORMAT AVANT D'ACCEPTER UN FICHIER (CLIC OU GLISSER-DÉPOSER)
    setFile(file, kind) {
      if (!file) return;
      const accepted = kind === 'logo' ? ['image/png', 'image/jpeg'] : ['application/pdf'];
      if (!accepted.includes(file.type)) {
        this.fileError = kind === 'logo'
          ? this.$t('adhesion.form.logoFormatError')
          : this.$t('adhesion.form.certificateFormatError');
        return;
      }
      this.fileError = null;
      if (kind === 'logo') {
        this.logoSelected = file;
      } else {
        this.attestationSelected = file;
      }
    },

    onDrop(event, kind) {
      this.dragOver = null;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      this.setFile(file, kind);
    },

    formatSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      return bytes < 1024 * 1024
        ? Math.max(1, Math.round(bytes / 1024)) + ' Ko'
        : (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
    },

    clearLogo() {
      this.logoSelected = null;
      if (this.$refs.logoInput) this.$refs.logoInput.value = '';
    },

    onAttestationChange(e) {
      const file = e.target.files && e.target.files[0];
      this.setFile(file, 'attestation');
    },

    clearAttestation() {
      this.attestationSelected = null;
      if (this.$refs.attestationInput) this.$refs.attestationInput.value = '';
    },

    convertirEnBase64(file, documentModel) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        documentModel.content = e.target.result.split(",")[1];
      };
    },

    convertirEnBase64Async(file) {
      return new Promise((resolve, reject) => {
        if (!file) return resolve(null);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          try {
            const result = e && e.target && e.target.result;
            if (!result) return resolve(null);
            return resolve(String(result).split(",")[1]);
          } catch (err) {
            return reject(err);
          }
        };
        reader.onerror = (e) => reject(e);
      });
    },

    revokePreviewUrls() {
      if (this.logoPreviewUrl) {
        try {
          URL.revokeObjectURL(this.logoPreviewUrl);
        } catch (e) {
          // ignore
        }
      }
      if (this.attestationPreviewUrl) {
        try {
          URL.revokeObjectURL(this.attestationPreviewUrl);
        } catch (e) {
          // ignore
        }
      }
      this.logoPreviewUrl = null;
      this.attestationPreviewUrl = null;
    },

    returnTypeMime(file) {
      var formatTypeMime = "";
      if (file.type == "image/png") {
        formatTypeMime = "FORMAT_PNG";
      }
      if (file.type == "image/jpeg") {
        formatTypeMime = "FORMAT_PNG";
      }
      if (file.type == "image/gif") {
        formatTypeMime = "FORMAT_GIF";
      }
      if (file.type == "application/pdf") {
        formatTypeMime = "FORMAT_PDF";
      }
      return formatTypeMime;
    },

    // OBTENIR LA LISTE DES VILLES DISPONIBLES
    async obtenirListeVillesDispo() {
      await axios
        .post(API_OBTENIR_LISTE_DES_VILLES_DISPONIBLE, this.defaultObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          this.villesList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // CREATION D'UNE DEMANDE D'ADHESION
    async creerDemandeAdhesion() {
      this.logoDocumentModel.compagnieTransportRaisonSociale =
        this.compagnieTransport.raisonSociale;
      this.logoDocumentModel.typeDocument = "LOGO";
      this.logoDocumentModel.typeMime = this.logoSelected
        ? this.returnTypeMime(this.logoSelected)
        : null;

      this.attestationTransportDocumentModel.typeDocument =
        "ATTESTATION_TRANSPORT";
      this.attestationTransportDocumentModel.compagnieTransportRaisonSociale =
        this.compagnieTransport.raisonSociale;
      this.attestationTransportDocumentModel.typeMime = this.attestationSelected
        ? this.returnTypeMime(this.attestationSelected)
        : null;

      this.overlay = true;
      try {
        this.logoDocumentModel.content = await this.convertirEnBase64Async(
          this.logoSelected
        );
        this.attestationTransportDocumentModel.content =
          await this.convertirEnBase64Async(this.attestationSelected);

        this.compagnieTransport.documentDTOS = [];
        if (this.attestationSelected && this.attestationTransportDocumentModel.content) {
          this.compagnieTransport.documentDTOS.push(
            this.attestationTransportDocumentModel
          );
        }
        if (this.logoSelected && this.logoDocumentModel.content) {
          this.compagnieTransport.documentDTOS.push(this.logoDocumentModel);
        }

        this.objectContainList.datas = [];
        this.objectContainList.datas.push(this.compagnieTransport);

        await axios
          .post(API_CREER_COMPAGNIE_TRANSPORT, this.objectContainList, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          })
          .then((response) => {
            if (response.status == 200) {
              if (response.data.status.code == 800) {
                this.successMsg = response.data.status.message;
                showSuccessMessage();
                // La saisie n'est vidée qu'en cas de succès
                this.reinitForm();
              } else {
                this.errorMsg = response.data.status.message;
                showErrorMessage();
              }
            } else if (response.status == 204) {
              this.warningMsg = this.$t('adhesion.errors.creationError');
              showWarningMessage();
            } else {
              this.errorMsg = this.$t('adhesion.errors.creationImpossible');
              showErrorMessage();
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          });
      } catch (e) {
        this.errorMsg = e;
        showErrorMessage();
      } finally {
        this.overlay = false;
        this.objectContainList.datas = [];
      }
    },

    // SOUMETTRE LE FORMULAIRE
    submitForm() {
      if (this.overlay) return;
      this.$v.$touch();
      if (this.$v.compagnieTransport.$invalid) {
        this.errorMsg = this.$t('adhesion.errors.missingInfo');
        showErrorMessage();
      } else {
        this.creerDemandeAdhesion();
      }
    },
  },

  computed: {
    // CHAMPS OBLIGATOIRES ET DOCUMENTS, AFFICHÉS DANS LE RÉCAPITULATIF
    checklist() {
      const v = this.$v.compagnieTransport;
      return [
        { label: this.$t('adhesion.businessName'), done: !!this.compagnieTransport.raisonSociale },
        { label: this.$t('adhesion.acronym'), done: !!this.compagnieTransport.sigle },
        { label: this.$t('adhesion.yourEmail'), done: !!this.compagnieTransport.email && v.email.email },
        { label: this.$t('adhesion.phoneNumber'), done: !!this.compagnieTransport.telephone },
        { label: this.$t('adhesion.locality'), done: !!this.compagnieTransport.villeDesignation },
        { label: this.$t('adhesion.logo'), done: !!this.logoSelected, optional: true },
        { label: this.$t('adhesion.certificate'), done: !!this.attestationSelected, optional: true },
      ];
    },

    completion() {
      const required = this.checklist.filter((item) => !item.optional);
      return Math.round((required.filter((item) => item.done).length / required.length) * 100);
    },

    raisonSocialeCompagnieErrors() {
      const errors = [];
      if (!this.$v.compagnieTransport.raisonSociale.$dirty) return errors;
      !this.$v.compagnieTransport.raisonSociale.required &&
        errors.push(this.$t('adhesion.errors.businessNameRequired'));
      return errors;
    },

    sigleCompagnieErrors() {
      const errors = [];
      if (!this.$v.compagnieTransport.sigle.$dirty) return errors;
      !this.$v.compagnieTransport.sigle.required &&
        errors.push(this.$t('adhesion.errors.acronymRequired'));
      return errors;
    },

    villeCompagnieErrors() {
      const errors = [];
      if (!this.$v.compagnieTransport.villeDesignation.$dirty) return errors;
      !this.$v.compagnieTransport.villeDesignation.required &&
        errors.push(this.$t('adhesion.errors.localityRequired'));
      return errors;
    },

    telephoneCompagnieErrors() {
      const errors = [];
      if (!this.$v.compagnieTransport.telephone.$dirty) return errors;
      !this.$v.compagnieTransport.telephone.required &&
        errors.push(this.$t('adhesion.errors.phoneRequired'));
      return errors;
    },
    emailCompagnieErrors() {
      const errors = [];
      if (!this.$v.compagnieTransport.email.$dirty) return errors;
      !this.$v.compagnieTransport.email.required &&
        errors.push(this.$t('adhesion.errors.emailRequired'));
      !this.$v.compagnieTransport.email.email && 
        errors.push(this.$t('adhesion.errors.emailInvalid'));
      return errors;
    },
  },

  mounted() {
    this.obtenirListeVillesDispo();
  },

  watch: {
    logoSelected(val) {
      if (this.logoPreviewUrl) {
        try {
          URL.revokeObjectURL(this.logoPreviewUrl);
        } catch (e) {
          this.errorMsg = e;
          showErrorMessage();
        }
      }
      this.logoPreviewUrl = val ? URL.createObjectURL(val) : null;
    },
    attestationSelected(val) {
      if (this.attestationPreviewUrl) {
        try {
          URL.revokeObjectURL(this.attestationPreviewUrl);
        } catch (e) {
          this.errorMsg = e;
          showErrorMessage();
        }
      }
      this.attestationPreviewUrl = val ? URL.createObjectURL(val) : null;
    },
  },

  beforeDestroy() {
    this.revokePreviewUrls();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

/* SECTIONS NUMÉROTÉES */
.ca-section {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 800;
  color: $ov-navy;

  &--spaced {
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid $ov-line;
  }
}

.ca-section__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  color: #ffffff;
  background: $ov-green;
}

.ca-section__optional {
  font-size: 12px;
  font-weight: 600;
  color: $ov-subtle;
}

/* DÉPÔT DE DOCUMENTS */
.ca-drop {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 170px;
  overflow: hidden;
  border: 1px dashed rgba(15, 23, 42, 0.2);
  border-radius: 12px;
  background: $ov-soft;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover,
  &:focus-visible,
  &--over {
    border-color: $ov-green;
    background: rgba(21, 158, 114, 0.05);
  }

  &--over {
    box-shadow: 0 0 0 3px rgba(21, 158, 114, 0.15);
  }

  &--filled {
    border-style: solid;
    border-color: $ov-line;
    background: #ffffff;
    cursor: default;
  }
}

.ca-drop__img {
  display: block;
  width: 100%;
  height: 100%;
  padding: 12px;
  object-fit: contain;
  pointer-events: none;
}

.ca-drop__pdf {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  pointer-events: none;
}

.ca-drop__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: $ov-muted;
  pointer-events: none;
  user-select: none;

  small {
    font-size: 12px;
    font-weight: 500;
    color: $ov-subtle;
  }

  .v-icon {
    color: $ov-green !important;
  }
}

.ca-file {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  font-size: 12px;
}

.ca-file__name {
  flex: 1;
  min-width: 0;
  font-weight: 700;
  color: $ov-ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ca-file__size {
  color: $ov-subtle;
}

.ca-file__action {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 700;
  color: $ov-green-dark;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &--danger {
    color: $ov-danger;
  }
}

/* RÉCAPITULATIF */
.ca-checklist {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ca-checklist__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  font-size: 13px;
  color: $ov-muted;
  border-bottom: 1px solid $ov-line;

  &:last-child {
    border-bottom: none;
  }

  &--done {
    color: $ov-ink;
    font-weight: 700;
  }
}

.ca-checklist__mark {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(15, 23, 42, 0.18);

  .ca-checklist__item--done & {
    background: $ov-green;
    border-color: $ov-green;
  }
}

.ca-checklist__label {
  flex: 1;
}

.ca-checklist__optional {
  font-size: 11px;
  font-weight: 600;
  color: $ov-subtle;
}
</style>
